import { Vault } from "../../libs/storage.js";
import { ExtraParamsValues } from "./ExtraParams";
import { UserData } from "./Tracking";
import { Utils } from "./Utils";

export { Scrub, ScrubRule };

interface ScrubRule {
  active: string;
  priority: string;
  weight: string;
  scrub_target: {
    conditions: string;
  };
  scrub_percentage: string;
  scrub_replace: { [key: string]: string | null };
}

interface ScrubTargetCondition {
  AND?: ScrubTargetCondition[];
  OR?: ScrubTargetCondition[];
  [key: string]: unknown;
}

class Scrub {
  private _scrubRules: ScrubRule[];
  private _userData: UserData | null = null;
  private _utils: Utils;
  private _currentExtraParams: ExtraParamsValues;

  constructor(
    scrubRules: ScrubRule[],
    utils: Utils,
    currentExtraParams: ExtraParamsValues,
  ) {
    this._currentExtraParams = currentExtraParams;
    this._utils = utils;
    this._scrubRules = scrubRules;
  }

  applyScrub(userData: UserData) {
    this._userData = userData;
    this._userData.country_code = this._utils.getUserCountry();
    this._debug("[DEBUG] Datos del usuario:");
    this._debug(this._userData);

    const matchingRules = this._filterAndSortRules(this._scrubRules);
    this._debug("[DEBUG] Reglas que hacen match:");
    this._debug(matchingRules);

    const selectedRule = this._selectRuleByWeightAndPriority(matchingRules);
    this._debug("[DEBUG] Regla seleccionada:");
    this._debug(selectedRule);

    this._applySelectedRule(selectedRule);
  }

  private _evaluateConditions(
    conditions: ScrubTargetCondition,
    data: UserData,
  ) {
    if (conditions.AND) {
      return conditions.AND.every((condition) =>
        this._evaluateConditions(condition, data),
      );
    } else if (conditions.OR) {
      return conditions.OR.some((condition) =>
        this._evaluateConditions(condition, data),
      );
    } else {
      const field = Object.keys(conditions)[0];
      const operation = Object.keys(conditions[field])[0];
      const value = conditions[field][operation];

      switch (operation) {
        case "equals":
          return data[field] === value;
        case "not_equals":
          return data[field] !== value;
        case "contains":
          return data[field].includes(value);
        case "not_contains":
          return !data[field].includes(value);
        case "starts_with":
          return data[field].startsWith(value);
        case "not_starts_with":
          return !data[field].startsWith(value);
        case "ends_with":
          return data[field].endsWith(value);
        case "not_ends_with":
          return !data[field].endsWith(value);
        default:
          return false;
      }
    }
  }

  private _filterAndSortRules(rules: ScrubRule[]) {
    return rules
      .filter(
        (rule) =>
          rule.active === "1" &&
          this._evaluateConditions(
            JSON.parse(rule.scrub_target.conditions),
            this._userData,
          ),
      )
      .sort((a, b) => parseInt(a.priority) - parseInt(b.priority));
  }

  private _selectRuleByWeightAndPriority(rules: ScrubRule[]) {
    if (!rules.length) return null;
    const highestPriority = rules[0].priority;
    const highestPriorityRules = rules.filter(
      (rule) => rule.priority === highestPriority,
    );

    if (highestPriorityRules.length === 1) {
      return highestPriorityRules[0];
    }

    // Aplicar algoritmo de pesos
    return this._selectRuleByWeight(highestPriorityRules);
  }

  private _selectRuleByWeight(rules: ScrubRule[]) {
    this._debug("[DEBUG] Tenemos que filtrar por pesos");
    const totalWeight = rules.reduce(
      (sum, rule) => sum + parseInt(rule.weight),
      0,
    );
    this._debug("[DEBUG] Peso total: " + totalWeight);
    let cumulativeWeight = 0;

    // Genera un umbral aleatorio entre 0 y el peso total
    const threshold = Math.random() * totalWeight;
    this._debug("[DEBUG] Umbral aleatorio: " + threshold);

    // Itera sobre las reglas y suma sus pesos
    for (const rule of rules) {
      cumulativeWeight += parseInt(rule.weight);
      this._debug("[DEBUG] Regla:");
      this._debug(rule);
      this._debug("[DEBUG] Peso acumulado: " + cumulativeWeight);

      // Devuelve la regla cuando el peso acumulado supera el umbral
      if (cumulativeWeight >= threshold) {
        this._debug("[DEBUG] Nos quedamos con esta regla");
        return rule;
      }
    }
  }

  private _applySelectedRule(rule: ScrubRule | null) {
    if (!rule) {
      return;
    }

    const scrubPercentage = parseInt(rule.scrub_percentage);
    const raffleNumber = Math.floor(Math.random() * 100);

    if (raffleNumber < scrubPercentage) {
      Object.keys(rule.scrub_replace).forEach((key) => {
        if (
          rule.scrub_replace[key] !== null &&
          this._userData.hasOwnProperty(key)
        ) {
          this._userData[key] = rule.scrub_replace[key];
        }
      });

      Vault.set(
        "t_utm_values",
        {
          utm_source: this._userData.utm_source,
          utm_medium: this._userData.utm_medium,
          utm_campaign: this._userData.utm_campaign,
          utm_term: this._userData.utm_term,
          utm_content: this._userData.utm_content,
          utm_id: this._userData.utm_id,
        },
        { expires: "365d" },
      );

      const t_extra_params = {};
      Object.keys(this._currentExtraParams).forEach((key) => {
        t_extra_params[key] =
          rule.scrub_replace[key] ?? this._currentExtraParams[key];
      });

      Vault.set("t_extra_params", t_extra_params, { expires: "365d" });
    }
  }

  private _debug(message: string | object) {
    if (Vault.get("is_admin")) {
      console.log(message);
    }
  }
}
